// Дашборды: менеджер (вкладки, избранное, копирование, удаление), сохранение и загрузка.

import type { DashboardDefinition, DashboardOut, DashboardSummary } from '../types';
import { api } from '../api';
import { authRequired, ensureGeoTicket, hasPermission, isAnonymous, isSignedIn, PERM_DASHBOARDS_MANAGE_ALL } from '../auth';
import { tr } from '../ui-i18n';
import { formatDateTime, t } from '../i18n';

import type { DashboardManagerTab } from './state';
import { applyTheme, currentTheme } from './theming';
import { blankDashboard, clone, normalizeDashboard } from './model';
import { controls, element } from './controls';
import { currentLocale } from './locale';
import { errorMessage, markDirty, setStatus, updateDashboardHeading } from './status';
import { escapeHtml, icon, refreshIcons } from './dom';
import { renderDashboardGrid } from './widgets';
import { resetWidgetForm } from './widget-editor';
import { setViewMode } from './workspace';
import { state } from './state';
import { syncLayoutsFromGrid } from './canvas';

export function populateDashboardSelect(): void {
  const current = state.currentTemplateId ?? state.currentDashboardId ?? '';
  const locale = currentLocale();
  const option = (dashboard: DashboardSummary): string => (
    `<option value="${escapeHtml(dashboard.id)}">${escapeHtml(dashboard.name)}</option>`
  );
  controls.dashboardSelect.innerHTML = [
    `<option value="">${escapeHtml(t(locale, 'unsavedDashboard'))}</option>`,
    ...state.dashboards.map(option),
  ].join('');
  controls.dashboardSelect.value = current;
  syncVisibilityChip();
  if (controls.dashboardManagerDialog.open) renderDashboardManager();
}

/** Вкладка «Все» — администратору (видит и чужие личные дашборды). */
function canSeeAllTab(): boolean {
  return isSignedIn() && hasPermission(PERM_DASHBOARDS_MANAGE_ALL);
}

function availableTabs(): DashboardManagerTab[] {
  if (isAnonymous()) return ['shared'];
  return canSeeAllTab() ? ['mine', 'shared', 'all'] : ['mine', 'shared'];
}

export function dashboardInTab(dashboard: DashboardSummary, tab: DashboardManagerTab): boolean {
  if (tab === 'mine') return dashboard.is_owner && !dashboard.is_template;
  if (tab === 'shared') return dashboard.visibility === 'public' || dashboard.is_template;
  return true;
}

function visibilityLabel(dashboard: Pick<DashboardSummary, 'visibility'>): string {
  return dashboard.visibility === 'public' ? tr('Общий') : tr('Личный');
}

/** Плашка «Личный / Общий» у названия: видна вошедшему, кнопка — если можно переключить. */
export function syncVisibilityChip(): void {
  const chip = controls.dashboardVisibility;
  const summary = currentDashboardSummary();
  if (!isSignedIn() || !summary || summary.is_template) {
    chip.hidden = true;
    return;
  }
  const isPublic = summary.visibility === 'public';
  const target = isPublic ? tr('Сделать личным') : tr('Сделать общим');
  chip.hidden = false;
  chip.disabled = !summary.can_change_visibility;
  chip.classList.toggle('is-public', isPublic);
  chip.innerHTML = `${icon(isPublic ? 'globe' : 'lock')}<span>${escapeHtml(visibilityLabel(summary))}</span>`;
  const hint = summary.can_change_visibility
    ? `${visibilityLabel(summary)} · ${target}`
    : isPublic ? tr('Общий: виден всем, включая гостей без входа') : tr('Личный: виден только вам');
  chip.title = hint;
  chip.setAttribute('aria-label', hint);
  refreshIcons(chip);
}

export async function toggleDashboardVisibility(id: string): Promise<void> {
  const dashboard = state.dashboards.find((item) => item.id === id);
  if (!dashboard?.can_change_visibility) return;
  const visibility = dashboard.visibility === 'public' ? 'private' : 'public';
  try {
    await api.updateDashboardMetadata(id, { visibility });
    await refreshDashboardList();
    renderDashboardManager();
    setStatus(visibility === 'public'
      ? tr('Дашборд стал общим: его видят все, включая гостей без входа.')
      : tr('Дашборд стал личным: его видите только вы.'), 'success');
  } catch (error) {
    setStatus(tr('Не удалось изменить доступ: {0}', errorMessage(error)), 'error');
  }
}

export function currentDashboardKey(): string | null {
  return state.currentTemplateId ?? state.currentDashboardId;
}

export function currentDashboardSummary(): DashboardSummary | null {
  const current = currentDashboardKey();
  return current ? state.dashboards.find((dashboard) => dashboard.id === current) ?? null : null;
}

export function canRenameCurrentDashboard(): boolean {
  if (isAnonymous()) return false;
  if (!state.currentDashboardId && !state.currentTemplateId) return true;
  return currentDashboardSummary()?.can_rename === true;
}

export function dashboardActionButton(
  dashboard: DashboardSummary,
  action: string,
  iconName: string,
  label: string,
  disabled = false,
): string {
  return `<button class="icon-button dashboard-manager-action" type="button" data-dashboard-action="${action}" data-dashboard-id="${escapeHtml(dashboard.id)}" aria-label="${escapeHtml(label)}" title="${escapeHtml(label)}" ${disabled ? 'disabled' : ''}>${icon(iconName)}<span class="dashboard-manager-action-label">${escapeHtml(label)}</span></button>`;
}

export function renderDashboardManager(): void {
  const current = currentDashboardKey();
  const locale = currentLocale();
  const query = controls.dashboardManagerSearch.value.trim().toLocaleLowerCase(locale === 'en' ? 'en-US' : 'ru-RU');
  const tab = state.dashboardManagerTab;
  const demoTab = tab === 'shared';
  renderDashboardManagerTabs();
  const anonymous = isAnonymous();
  const showOwner = tab === 'all';
  const dashboards = state.dashboards.filter((dashboard) =>
    dashboardInTab(dashboard, tab)
    && (!query || dashboard.name.toLocaleLowerCase(locale === 'en' ? 'en-US' : 'ru-RU').includes(query)),
  );
  if (!dashboards.length) {
    const title = demoTab && !query ? t(locale, 'noDemoDashboards') : t(locale, 'noDashboardsFound');
    controls.dashboardManagerList.innerHTML = `
      <div class="dashboard-manager-empty">
        <strong>${escapeHtml(title)}</strong>
        ${demoTab && !query ? '' : `<p>${escapeHtml(t(locale, 'noDashboardsFoundHint'))}</p>`}
      </div>`;
    return;
  }
  controls.dashboardManagerList.innerHTML = dashboards.map((dashboard) => {
    const isCurrent = dashboard.id === current;
    const pendingDelete = state.pendingDashboardDeleteId === dashboard.id;
    const favoriteLabel = dashboard.favorite ? t(locale, 'removeFavorite') : t(locale, 'addFavorite');
    const badges = authRequired() ? [
      dashboard.is_template
        ? `<span class="dashboard-manager-badge is-public">${escapeHtml(tr('Шаблон'))}</span>`
        : `<span class="dashboard-manager-badge${dashboard.visibility === 'public' ? ' is-public' : ''}">${escapeHtml(visibilityLabel(dashboard))}</span>`,
      showOwner && !dashboard.is_owner && !dashboard.is_template
        ? `<span class="dashboard-manager-badge" data-i18n-skip>${escapeHtml(tr('владелец: {0}', dashboard.owner || '—'))}</span>`
        : '',
    ].join('') : '';
    const visibilityAction = !anonymous && authRequired() && dashboard.can_change_visibility
      ? dashboardActionButton(
        dashboard,
        'visibility',
        dashboard.visibility === 'public' ? 'lock' : 'globe',
        dashboard.visibility === 'public' ? tr('Сделать личным') : tr('Сделать общим'),
      )
      : '';
    return `
      <article class="dashboard-manager-item${isCurrent ? ' is-current' : ''}" data-dashboard-row="${escapeHtml(dashboard.id)}">
        ${anonymous
          ? '<span class="dashboard-manager-star" aria-hidden="true"></span>'
          : `<button class="dashboard-manager-star${dashboard.favorite ? ' is-favorite' : ''}" type="button" data-dashboard-action="favorite" data-dashboard-id="${escapeHtml(dashboard.id)}" aria-label="${escapeHtml(favoriteLabel)}" title="${escapeHtml(favoriteLabel)}" aria-pressed="${dashboard.favorite}">${icon('star')}</button>`}
        <div class="dashboard-manager-main">
          <button class="dashboard-manager-open" type="button" data-dashboard-action="open" data-dashboard-id="${escapeHtml(dashboard.id)}">
            <span class="dashboard-manager-name">${escapeHtml(dashboard.name)}</span>
            <span class="dashboard-manager-date">${escapeHtml(formatDateTime(dashboard.updated_at, locale))}${isCurrent ? ` · ${escapeHtml(t(locale, 'dashboardOpened'))}` : ''}</span>
          </button>
          ${badges ? `<div class="dashboard-manager-badges">${badges}</div>` : ''}
        </div>
        <div class="dashboard-manager-actions">
          ${pendingDelete
            ? `<div class="dashboard-delete-confirm" role="group" aria-label="${escapeHtml(t(locale, 'confirmDelete'))}">
                <button class="button button-danger" type="button" data-dashboard-action="confirm-delete" data-dashboard-id="${escapeHtml(dashboard.id)}">${escapeHtml(t(locale, 'confirmDelete'))}</button>
                <button class="button button-secondary" type="button" data-dashboard-action="cancel-delete" data-dashboard-id="${escapeHtml(dashboard.id)}">${escapeHtml(t(locale, 'cancelDelete'))}</button>
              </div>`
            : anonymous ? '' : `${visibilityAction}
              ${dashboardActionButton(dashboard, 'copy', 'copy-plus', t(locale, 'copyDashboard'), !dashboard.can_copy)}
              ${dashboard.can_rename ? dashboardActionButton(dashboard, 'rename', 'pencil', t(locale, 'renameDashboard')) : ''}
              ${dashboard.can_delete ? dashboardActionButton(dashboard, 'delete', 'trash-2', t(locale, 'deleteDashboard')) : ''}`}
        </div>
      </article>`;
  }).join('');
  refreshIcons(controls.dashboardManagerList);
}

export function renderDashboardManagerTabs(): void {
  const locale = currentLocale();
  const tabs = availableTabs();
  if (!tabs.includes(state.dashboardManagerTab)) state.dashboardManagerTab = tabs[0] ?? 'shared';
  const count = (tab: DashboardManagerTab): number => state.dashboards.filter((dashboard) => dashboardInTab(dashboard, tab)).length;
  const labels: Record<DashboardManagerTab, string> = {
    mine: t(locale, 'dashboardsTabMine'),
    shared: `${t(locale, 'dashboardsTabDemo')} · ${count('shared')}`,
    all: `${t(locale, 'dashboardsTabAll')} · ${count('all')}`,
  };
  controls.dashboardManagerTabs.querySelectorAll<HTMLButtonElement>('[data-dashboard-tab]').forEach((tab) => {
    const name = tab.dataset.dashboardTab as DashboardManagerTab;
    const selected = name === state.dashboardManagerTab;
    tab.hidden = !tabs.includes(name);
    tab.textContent = labels[name];
    tab.setAttribute('aria-selected', String(selected));
    tab.tabIndex = selected ? 0 : -1;
    tab.classList.toggle('is-active', selected);
  });
  controls.dashboardManagerList.setAttribute('aria-labelledby', `dashboard-manager-tab-${state.dashboardManagerTab}`);
  controls.dashboardManagerDemoHint.hidden = state.dashboardManagerTab !== 'shared';
  controls.dashboardManagerDemoHint.textContent = isAnonymous()
    ? tr('Общие дашборды и шаблоны открыты для просмотра без входа. Войдите, чтобы создавать свои дашборды и копировать общие.')
    : t(locale, 'demoDashboardsHint');
}

/** Следующая видимая вкладка для стрелок клавиатуры. */
export function neighbourDashboardManagerTab(key: string): DashboardManagerTab {
  const tabs = availableTabs();
  const index = Math.max(0, tabs.indexOf(state.dashboardManagerTab));
  if (key === 'Home') return tabs[0] ?? 'mine';
  if (key === 'End') return tabs[tabs.length - 1] ?? 'mine';
  const step = key === 'ArrowLeft' ? -1 : 1;
  return tabs[(index + step + tabs.length) % tabs.length] ?? 'mine';
}

export function selectDashboardManagerTab(tab: DashboardManagerTab, focus = false): void {
  state.dashboardManagerTab = tab;
  state.pendingDashboardDeleteId = null;
  renderDashboardManager();
  if (focus) controls.dashboardManagerTabs.querySelector<HTMLButtonElement>(`[data-dashboard-tab="${tab}"]`)?.focus();
}

export async function openDashboardManager(): Promise<void> {
  await refreshDashboardList();
  state.pendingDashboardDeleteId = null;
  // Открываем раздел, в котором лежит текущий дашборд, — иначе он «пропадает».
  const summary = currentDashboardSummary();
  state.dashboardManagerTab = isAnonymous() ? 'shared'
    : !summary || dashboardInTab(summary, 'mine') ? 'mine'
      : dashboardInTab(summary, 'shared') ? 'shared'
        : canSeeAllTab() ? 'all' : 'mine';
  controls.dashboardManagerSearch.value = '';
  renderDashboardManager();
  controls.dashboardManagerDialog.showModal();
  controls.dashboardManagerSearch.focus();
}

export function closeDashboardManager(): void {
  state.pendingDashboardDeleteId = null;
  controls.dashboardManagerDialog.close();
}

export function beginDashboardManagerRename(id: string): void {
  const dashboard = state.dashboards.find((item) => item.id === id);
  if (!dashboard?.can_rename) {
    setStatus('Скопируйте дашборд, чтобы переименовать.');
    return;
  }
  const row = controls.dashboardManagerList.querySelector<HTMLElement>(`[data-dashboard-row="${CSS.escape(id)}"]`);
  const main = row?.querySelector<HTMLElement>('.dashboard-manager-main');
  if (!row || !main) return;
  main.innerHTML = `
    <label class="dashboard-manager-rename">
      <span class="sr-only">Новое название</span>
      <input data-dashboard-rename-input="${escapeHtml(id)}" value="${escapeHtml(dashboard.name)}" maxlength="300" />
    </label>`;
  const input = main.querySelector<HTMLInputElement>('input');
  input?.focus();
  input?.select();
}

export async function renameDashboard(id: string, name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) {
    renderDashboardManager();
    return;
  }
  try {
    const wasDirty = state.dirty;
    const updated = await api.updateDashboardMetadata(id, { name: trimmed });
    await refreshDashboardList();
    if (id === currentDashboardKey()) {
      controls.dashboardName.value = updated.name;
      state.definition.title = updated.name;
      updateDashboardHeading();
      markDirty(wasDirty);
    }
    renderDashboardManager();
    setStatus(`Дашборд переименован: ${updated.name}`, 'success');
  } catch (error) {
    renderDashboardManager();
    setStatus(`Не удалось переименовать: ${errorMessage(error)}`, 'error');
  }
}

export async function toggleDashboardFavorite(id: string): Promise<void> {
  const dashboard = state.dashboards.find((item) => item.id === id);
  if (!dashboard) return;
  const nextFavorite = !dashboard.favorite;
  dashboard.favorite = nextFavorite;
  renderDashboardManager();
  try {
    await api.updateDashboardMetadata(id, { favorite: nextFavorite });
    await refreshDashboardList();
    renderDashboardManager();
    setStatus(nextFavorite ? t(currentLocale(), 'favoriteAdded') : t(currentLocale(), 'favoriteRemoved'), 'success');
  } catch (error) {
    dashboard.favorite = !nextFavorite;
    renderDashboardManager();
    setStatus(`Не удалось обновить избранное: ${errorMessage(error)}`, 'error');
  }
}

export async function copyDashboardFromManager(id: string): Promise<void> {
  const dashboard = state.dashboards.find((item) => item.id === id);
  if (!dashboard) return;
  try {
    const copied = await api.copyDashboard(id, `${dashboard.name} - копия`);
    await refreshDashboardList();
    closeDashboardManager();
    await loadDashboard(copied.id, true);
    setStatus(t(currentLocale(), 'copyOpenedStatus', { name: copied.name }), 'success');
  } catch (error) {
    setStatus(`Не удалось скопировать: ${errorMessage(error)}`, 'error');
  }
}

export function resetToUnsavedDashboard(): void {
  // Аноним собственных дашбордов не создаёт: пустой холст ему ни к чему.
  if (isAnonymous()) return;
  state.currentDashboardId = null;
  state.currentTemplateId = null;
  const title = t(currentLocale(), 'newDashboardTitle');
  state.definition = blankDashboard(title);
  controls.dashboardName.value = title;
  controls.dashboardSelect.value = '';
  setViewMode(false);
  applyTheme();
  updateDashboardHeading();
  renderDashboardGrid();
  resetWidgetForm();
  markDirty(true);
}

export function requestDashboardDelete(id: string): void {
  state.pendingDashboardDeleteId = id;
  renderDashboardManager();
  setStatus(t(currentLocale(), 'confirmDeleteHint'));
}

export function cancelDashboardDelete(): void {
  state.pendingDashboardDeleteId = null;
  renderDashboardManager();
}

export async function deleteDashboardFromManager(id: string): Promise<void> {
  const dashboard = state.dashboards.find((item) => item.id === id);
  if (!dashboard?.can_delete) return;
  try {
    await api.deleteDashboard(id);
    state.pendingDashboardDeleteId = null;
    const deletedCurrent = id === currentDashboardKey();
    await refreshDashboardList();
    if (deletedCurrent) {
      resetToUnsavedDashboard();
    }
    renderDashboardManager();
    setStatus(`Дашборд удалён: ${dashboard.name}`, 'success');
  } catch (error) {
    setStatus(`Не удалось удалить: ${errorMessage(error)}`, 'error');
  }
}

export function startHeaderRename(): void {
  if (state.viewMode) {
    setStatus('Переименование доступно в режиме редактирования.');
    return;
  }
  if (!canRenameCurrentDashboard()) {
    setStatus('Скопируйте дашборд, чтобы переименовать.');
    return;
  }
  controls.dashboardName.hidden = false;
  controls.dashboardTitleButton.hidden = true;
  controls.dashboardName.focus();
  controls.dashboardName.select();
}

export async function finishHeaderRename(commit: boolean): Promise<void> {
  if (controls.dashboardName.hidden) return;
  controls.dashboardName.hidden = true;
  controls.dashboardTitleButton.hidden = false;
  if (!commit) {
    controls.dashboardName.value = state.definition.title;
    return;
  }
  const name = controls.dashboardName.value.trim() || state.definition.title || 'Новый дашборд';
  controls.dashboardName.value = name;
  if (state.currentDashboardId) {
    await renameDashboard(state.currentDashboardId, name);
    return;
  }
  updateDashboardHeading();
  markDirty(true);
}

export function isReadOnlySource(dashboard: DashboardOut): boolean {
  // Без права правки (шаблон, чужой общий у клиента, просмотр гостем)
  // дашборд открывается как источник: «Сохранить» создаёт свою копию.
  return dashboard.is_template === true || !dashboard.can_edit;
}

export function opensInPresentationMode(dashboard: DashboardOut): boolean {
  return isAnonymous() || isReadOnlySource(dashboard);
}

export function definitionForSave(): DashboardDefinition {
  syncLayoutsFromGrid();
  updateDashboardHeading();
  return { ...clone(state.definition), schemaVersion: 5, theme: currentTheme() };
}

export async function refreshDashboardList(): Promise<void> {
  state.dashboards = await api.dashboards();
  populateDashboardSelect();
}

export async function saveDashboard(): Promise<void> {
  const name = controls.dashboardName.value.trim();
  if (!name) {
    setStatus('Введите название дашборда', 'error');
    controls.dashboardName.focus();
    return;
  }
  const button = element<HTMLButtonElement>('#save-dashboard');
  button.disabled = true;
  setStatus('Сохранение…');
  try {
    const definition = definitionForSave();
    let saved: DashboardOut;
    if (state.currentDashboardId) {
      saved = await api.updateDashboard(state.currentDashboardId, name, definition);
    } else {
      saved = await api.createDashboard(name, definition);
      state.currentDashboardId = saved.id;
      state.currentTemplateId = null;
    }
    state.definition = normalizeDashboard(saved.definition, saved.name);
    controls.dashboardName.value = saved.name;
    applyTheme();
    await refreshDashboardList();
    markDirty(false);
    setStatus(`Дашборд сохранён ${formatDateTime(saved.updated_at, currentLocale())}`, 'success');
  } catch (error) {
    setStatus(`Ошибка сохранения: ${errorMessage(error)}`, 'error');
  } finally {
    button.disabled = false;
  }
}

export function startNewDashboard(): void {
  if (state.dirty && !window.confirm('Несохранённые изменения будут потеряны. Создать новый дашборд?')) return;
  resetToUnsavedDashboard();
  setStatus('Создан пустой дашборд');
}

export async function loadDashboard(id: string, skipConfirmation = false): Promise<void> {
  if (!id) {
    startNewDashboard();
    return;
  }
  if (!skipConfirmation && state.dirty && !window.confirm('Несохранённые изменения будут потеряны. Открыть другой дашборд?')) {
    controls.dashboardSelect.value = state.currentTemplateId ?? state.currentDashboardId ?? '';
    return;
  }
  setStatus('Загрузка дашборда…');
  try {
    const dashboard = await api.dashboard(id);
    // Шаблон и демонстрационный дашборд без права правки открываются как
    // источник: «Сохранить» создаёт собственную копию, а не 403 от PUT.
    const readOnlySource = isReadOnlySource(dashboard);
    state.currentDashboardId = readOnlySource ? null : dashboard.id;
    state.currentTemplateId = readOnlySource ? dashboard.id : null;
    state.definition = normalizeDashboard(dashboard.definition, dashboard.name);
    controls.dashboardName.value = dashboard.name;
    // Гостю ГИС отдаёт геометрию и тайлы только по пропуску этого дашборда.
    if (isAnonymous()) await ensureGeoTicket(dashboard.id);
    applyTheme();
    updateDashboardHeading();
    renderDashboardGrid();
    resetWidgetForm();
    markDirty(false);
    populateDashboardSelect();
    setViewMode(opensInPresentationMode(dashboard));
    setStatus(
      isAnonymous()
        ? tr('Открыт общий дашборд «{0}» — режим просмотра', dashboard.name)
        : !dashboard.is_template && readOnlySource ? t(currentLocale(), 'demoReadOnlyStatus') : `Открыт дашборд «${dashboard.name}»`,
      'success',
    );
  } catch (error) {
    setStatus(`Ошибка загрузки: ${errorMessage(error)}`, 'error');
  }
}
